import i18n from '../i18n';

// Supported languages - base list, can be extended dynamically
export const SUPPORTED_LANGS = ['pl', 'en', 'uk', 'de', 'fr', 'cs'];
export const DEFAULT_LANG = 'pl';

// Dynamic data cache (populated from API)
let dynamicLanguages = null;
let dynamicUrlMappings = {};

// Function to set dynamic languages from API
export const setDynamicLanguages = (langs) => {
  dynamicLanguages = langs.map(l => l.code);
  // Also cache URL mappings from API
  langs.forEach(l => {
    if (l.urlMappings && Object.keys(l.urlMappings).length > 0) {
      dynamicUrlMappings[l.code] = l.urlMappings;
    }
  });
};

// Function to check if language is supported (static OR dynamic)
export const isLanguageSupported = (lang) => {
  if (!lang || lang.length !== 2) return false;
  if (SUPPORTED_LANGS.includes(lang)) return true;
  if (dynamicLanguages && dynamicLanguages.includes(lang)) return true;
  // Accept any 2-letter code during initial load
  return true;
};

// Get URL mappings - prefer dynamic (from API) over static
export const getUrlMappings = (lang) => {
  // First check dynamic mappings from API
  if (dynamicUrlMappings[lang]) {
    return dynamicUrlMappings[lang];
  }
  // Fallback to static mappings
  return URL_MAPPINGS[lang] || URL_MAPPINGS['en'];
};

// URL mappings for each language - Polish uses Polish words, English uses English
export const URL_MAPPINGS = {
  pl: {
    home: '',
    park: 'park',
    book: 'rezerwuj',
    prices: 'cennik',
    passes: 'karnety',
    faq: 'faq',
    contact: 'kontakt',
    login: 'logowanie',
    register: 'rejestracja',
    account: 'konto',
    activities: 'aktywnosci',
    classes: 'aktywnosci/zajecia',
    birthdays: 'aktywnosci/urodziny',
    physio: 'aktywnosci/fizjoterapia',
    document: 'dokument',
    blog: 'blog',
    events: 'wydarzenia',
    forgotPassword: 'przypomnij-haslo',
    resetPassword: 'resetuj-haslo',
    paymentStatus: 'platnosc/status'
  },
  en: {
    home: '',
    park: 'park',
    book: 'book',
    prices: 'prices',
    passes: 'passes',
    faq: 'faq',
    contact: 'contact',
    login: 'login',
    register: 'register',
    account: 'account',
    activities: 'activities',
    classes: 'activities/classes',
    birthdays: 'activities/birthdays',
    physio: 'activities/physio',
    document: 'document',
    blog: 'blog',
    events: 'events',
    forgotPassword: 'forgot-password',
    resetPassword: 'reset-password',
    paymentStatus: 'payment/status'
  },
  uk: {
    home: '',
    park: 'park',
    book: 'book',
    prices: 'prices',
    passes: 'passes',
    faq: 'faq',
    contact: 'contact',
    login: 'login',
    register: 'register',
    account: 'account',
    activities: 'activities',
    classes: 'activities/classes',
    birthdays: 'activities/birthdays',
    physio: 'activities/physio',
    document: 'document',
    blog: 'blog',
    events: 'events',
    forgotPassword: 'forgot-password',
    resetPassword: 'reset-password',
    paymentStatus: 'payment/status'
  },
  de: {
    home: '',
    park: 'park',
    book: 'buchen',
    prices: 'preise',
    passes: 'karten',
    faq: 'faq',
    contact: 'kontakt',
    login: 'anmelden',
    register: 'registrieren',
    account: 'konto',
    activities: 'aktivitaten',
    classes: 'aktivitaten/kurse',
    birthdays: 'aktivitaten/geburtstage',
    physio: 'aktivitaten/physiotherapie',
    document: 'dokument',
    blog: 'blog',
    events: 'veranstaltungen',
    forgotPassword: 'passwort-vergessen',
    resetPassword: 'passwort-zurucksetzen',
    paymentStatus: 'zahlung/status'
  },
  fr: {
    home: '',
    park: 'park',
    book: 'reserver',
    prices: 'tarifs',
    passes: 'abonnements',
    faq: 'faq',
    contact: 'contact',
    login: 'connexion',
    register: 'inscription',
    account: 'compte',
    activities: 'activites',
    classes: 'activites/cours',
    birthdays: 'activites/anniversaires',
    physio: 'activites/physiotherapie',
    document: 'document',
    blog: 'blog',
    events: 'evenements',
    forgotPassword: 'mot-de-passe-oublie',
    resetPassword: 'reinitialiser-mot-de-passe',
    paymentStatus: 'paiement/statut'
  }
};

/**
 * Get localized path for a given route key
 * @param {string} key - Route key (e.g., 'park', 'prices', 'classes')
 * @param {string} lang - Language code (defaults to current i18n language)
 * @returns {string} - Full localized path with language prefix
 */
export const getLocalizedPath = (key, lang = null) => {
  const currentLang = lang || i18n.language || DEFAULT_LANG;
  const langPrefix = `/${currentLang}`; // Always use requested lang in URL
  
  // Get mappings - prefer dynamic from API, fallback to static
  const mappings = getUrlMappings(currentLang);
  
  // Handle document paths with slugs
  if (key.startsWith('document/')) {
    const slug = key.replace('document/', '');
    return `${langPrefix}/${mappings.document || 'document'}/${slug}`;
  }
  
  const path = mappings?.[key];
  
  if (path === undefined) {
    // If key not found, return as-is with language prefix
    return `${langPrefix}/${key}`;
  }
  
  return path ? `${langPrefix}/${path}` : langPrefix;
};

/**
 * Get the home path for a language
 * @param {string} lang - Language code
 * @returns {string} - Home path (e.g., '/pl' or '/en')
 */
export const getHomePath = (lang = null) => {
  const currentLang = lang || i18n.language || DEFAULT_LANG;
  // Allow any language that's either in static list or dynamic list
  return `/${currentLang}`;
};

/**
 * Switch language and navigate to equivalent page
 * @param {string} newLang - New language code
 * @param {string} currentPath - Current pathname
 * @returns {string} - New path with switched language
 */
export const switchLanguagePath = (newLang, currentPath) => {
  // Allow switching to any language (don't block unknown languages)
  
  // Extract current language from path
  const pathParts = currentPath.split('/').filter(Boolean);
  const currentLang = pathParts[0]?.length === 2 ? pathParts[0] : null;
  
  if (!currentLang) {
    // No language prefix, just add new one
    return `/${newLang}${currentPath}`;
  }
  
  // Get the path after language prefix
  const pathAfterLang = pathParts.slice(1).join('/');
  
  // Try to find the route key for this path in current language
  // Use dynamic mappings if available
  const currentMappings = getUrlMappings(currentLang);
  const newMappings = getUrlMappings(newLang);
  
  // Find which key this path corresponds to
  // Sort by path length (longest first) so 'aktywnosci/zajecia' matches before 'aktywnosci'
  let routeKey = null;
  const sortedEntries = Object.entries(currentMappings)
    .filter(([, value]) => value !== '')
    .sort((a, b) => b[1].length - a[1].length);
  for (const [key, value] of sortedEntries) {
    if (value === pathAfterLang || pathAfterLang.startsWith(value + '/')) {
      routeKey = key;
      break;
    }
  }
  
  if (routeKey && newMappings[routeKey] !== undefined) {
    // Handle paths with additional segments (like document/:slug)
    if (pathAfterLang.includes('/') && currentMappings[routeKey] && !currentMappings[routeKey].includes('/')) {
      const extraPath = pathAfterLang.substring(currentMappings[routeKey].length);
      return `/${newLang}/${newMappings[routeKey]}${extraPath}`;
    }
    const newPath = newMappings[routeKey];
    return newPath ? `/${newLang}/${newPath}` : `/${newLang}`;
  }
  
  // Fallback: just replace language prefix
  return `/${newLang}/${pathAfterLang}`;
};

/**
 * Check if a path is active (for navigation highlighting)
 * @param {string} itemPath - Navigation item path
 * @param {string} currentPath - Current location pathname
 * @returns {boolean}
 */
export const isPathActive = (itemPath, currentPath) => {
  // Remove any 2-letter language prefix from both paths for comparison
  const cleanItemPath = itemPath.replace(/^\/[a-z]{2}/, '');
  const cleanCurrentPath = currentPath.replace(/^\/[a-z]{2}/, '');
  
  if (cleanItemPath === '' || cleanItemPath === '/') {
    return cleanCurrentPath === '' || cleanCurrentPath === '/';
  }
  
  return cleanCurrentPath.startsWith(cleanItemPath);
};

/**
 * Get current language from URL path
 * @param {string} pathname - Current pathname
 * @returns {string} - Language code
 */
export const getLangFromPath = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const lang = pathParts[0];
  // Accept any 2-letter language code
  return lang?.length === 2 ? lang : DEFAULT_LANG;
};
